import React, { useState } from "react"

import { ChevronDown, ChevronLeft, ChevronRight, FileText } from "lucide-react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type {
  DriverLicenceRecord,
  LicenceCpcEntry,
  LicenceEndorsement,
  LicenceEntitlement,
  LicenceTachoCard,
} from "@/types/driverLicence"

interface DrivingLicenceSectionProps {
  driverLicenceRecords?: DriverLicenceRecord[]
}

const formatDate = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

const formatExpiryBadge = (value?: string | null) => {
  if (!value) {
    return {
      label: "N/A",
      className: "bg-muted text-muted-foreground border-muted",
    }
  }
  const timestamp = Date.parse(value)
  if (Number.isNaN(timestamp)) {
    return {
      label: value,
      className: "bg-muted text-muted-foreground border-muted",
    }
  }
  const diffDays = (timestamp - Date.now()) / (1000 * 60 * 60 * 24)
  if (diffDays < 0) {
    return {
      label: formatDate(value),
      className: "bg-red-100 text-red-800 border-red-200",
    }
  }
  if (diffDays < 90) {
    return {
      label: formatDate(value),
      className: "bg-yellow-100 text-yellow-800 border-yellow-200",
    }
  }
  return {
    label: formatDate(value),
    className: "bg-green-100 text-green-800 border-green-200",
  }
}

const mapEndorsements = (endorsements?: LicenceEndorsement[] | null) => {
  if (!endorsements) return []
  return endorsements.map((endorsement, idx) => ({
    id: `${endorsement.offenceCode ?? "endorsement"}-${idx}`,
    offenceCode: endorsement.offenceCode ?? "-",
    description: endorsement.offenceLegalLiteral ?? "-",
    offenceDate: formatDate(endorsement.offenceDate),
    convictionDate: formatDate(endorsement.convictionDate),
    expiryDate: formatDate(endorsement.penaltyPointsExpiryDate),
    penaltyPoints: endorsement.penaltyPoints ?? 0,
  }))
}

const ALL_CATEGORIES = [
  "A",
  "AM",
  "B",
  "B1",
  "BE",
  "C",
  "C1",
  "C1E",
  "CE",
  "D",
  "D1",
  "D1E",
  "F",
  "G",
  "H",
  "K",
  "L",
  "N",
  "P",
  "Q",
]

const mapEntitlements = (entitlements?: LicenceEntitlement[] | null) => {
  const entitlementMap = new Map<string, LicenceEntitlement[]>()

  // Group entitlements by category code (can have multiple entries per category)
  entitlements?.forEach((entitlement) => {
    if (entitlement.categoryCode) {
      const existing = entitlementMap.get(entitlement.categoryCode) || []
      existing.push(entitlement)
      entitlementMap.set(entitlement.categoryCode, existing)
    }
  })

  // Map all categories, using actual data if available, otherwise mark as not held
  return ALL_CATEGORIES.map((categoryCode) => {
    const categoryEntitlements = entitlementMap.get(categoryCode)

    if (categoryEntitlements && categoryEntitlements.length > 0) {
      // Sort by expiry date (latest first) - entries with dates come before those without
      const sortedEntitlements = [...categoryEntitlements].sort((a, b) => {
        const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : 0
        const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : 0
        return dateB - dateA // Latest first
      })

      // Use the latest (first after sorting) entitlement for badge color
      const latestEntitlement = sortedEntitlements[0]
      const expiryBadge = formatExpiryBadge(latestEntitlement.expiryDate)

      return {
        id: `${categoryCode}`,
        category: categoryCode,
        entries: sortedEntitlements.map((ent) => ({
          type: ent.categoryType ?? "-",
          legalLiteral: ent.categoryLegalLiteral ?? "-",
          expiryBadge: formatExpiryBadge(ent.expiryDate),
          fromDate: formatDate(ent.fromDate),
          expiryDate: ent.expiryDate,
          restrictions: ent.restrictions?.map((restriction) => {
            const code = restriction.restrictionCode ?? ""
            const literal = restriction.restrictionLiteral ?? ""
            if (code && literal) {
              return `${code} - ${literal}`
            }
            return literal || code || "-"
          }),
        })),
        badgeClass: expiryBadge.className,
        hasEntitlement: true,
      }
    } else {
      // Category not held by driver
      return {
        id: `${categoryCode}-not-held`,
        category: categoryCode,
        entries: [
          {
            type: "Not held",
            legalLiteral: "This category is not on the driver's licence",
            expiryBadge: { label: "N/A", className: "" },
            fromDate: "-",
            expiryDate: null,
            restrictions: undefined,
          },
        ],
        badgeClass:
          "bg-palette-jungleGreen200 text-palette-jungleGreen500 border-palette-jungleGreen300",
        hasEntitlement: false,
      }
    }
  })
}

const extractCpcEntries = (cpc?: { cpcs?: LicenceCpcEntry[] | null } | null) => {
  if (!cpc?.cpcs) return []
  return cpc.cpcs.map((entry, idx) => ({
    id: `cpc-${idx}`,
    lgvValidTo: formatDate(entry.lgvValidTo),
    pcvValidTo: formatDate(entry.pcvValidTo),
    national: entry.national ?? null,
  }))
}

const extractTachoCards = (holder?: { tachoCards?: LicenceTachoCard[] | null } | null) => {
  if (!holder?.tachoCards) return []
  return holder.tachoCards.map((card, idx) => ({
    id: `${card.cardNumber ?? "card"}-${idx}`,
    number: card.cardNumber ?? "-",
    status: card.cardStatus ?? "-",
    expiry: formatExpiryBadge(card.cardExpiryDate),
    start: formatDate(card.cardStartOfValidityDate),
  }))
}

const DrivingLicenceSection = ({ driverLicenceRecords }: DrivingLicenceSectionProps) => {
  const [latestRecord, ...olderRecords] = driverLicenceRecords ?? []
  const [isOpen, setIsOpen] = useState(true)

  const endorsements = latestRecord ? mapEndorsements(latestRecord.endorsements) : []
  const entitlements = latestRecord
    ? mapEntitlements(latestRecord.entitlement)
    : mapEntitlements(null)
  const cpcEntries = latestRecord ? extractCpcEntries(latestRecord.cpc) : []
  const tachoCards = latestRecord ? extractTachoCards(latestRecord.holder) : []

  const licenceStatusBadge = latestRecord
    ? formatExpiryBadge(latestRecord.token_valid_to)
    : {
        label: "N/A",
        className:
          "bg-palette-jungleGreen200 text-palette-jungleGreen500 border-palette-jungleGreen300",
      }

  return (
    <Card className="shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg font-semibold">Driving Licence</CardTitle>
              </div>
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-4 space-y-2">
            {latestRecord ? (
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Latest DVLA verification captured on {formatDate(latestRecord.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {latestRecord.licence_status && (
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                          Driving Status
                        </p>
                        <Badge
                          className={cn(
                            "font-semibold text-sm px-3 py-1 pointer-events-none",
                            licenceStatusBadge.className,
                          )}
                        >
                          {latestRecord.licence_status}
                        </Badge>
                      </div>
                    )}
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                        Endorsements
                      </p>
                      <div className="flex gap-4">
                        <div>
                          <p className="text-2xl font-bold">{endorsements.length}</p>
                          <p className="text-xs text-muted-foreground">offences</p>
                        </div>
                        <div className="border-l pl-4">
                          <p className="text-2xl font-bold">
                            {endorsements.reduce(
                              (total, endorsement) =>
                                total + (Number(endorsement.penaltyPoints) || 0),
                              0,
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">points</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground text-center">
                  No DVLA verification data available for this applicant.
                </p>
              </div>
            )}
            {latestRecord && (
              <>
                <div className="bg-background rounded-lg border p-4">
                  <h3 className="text-sm font-semibold mb-3">Driver Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Licence Number
                      </p>
                      <p className="font-mono text-sm font-medium">
                        {latestRecord.driving_licence_number ?? "-"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Driver Name
                      </p>
                      <p className="text-sm font-medium">
                        {[latestRecord.first_name, latestRecord.last_name]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {latestRecord.gender ?? "-"} · DOB {formatDate(latestRecord.date_of_birth)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Address
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {[
                          latestRecord.address_line1,
                          latestRecord.address_line2,
                          latestRecord.address_line5,
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </p>
                      <p className="text-xs text-muted-foreground font-medium">
                        {latestRecord.postcode ?? "-"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-background rounded-lg border p-4">
                  <h3 className="text-sm font-semibold mb-3">Licence Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Licence Type
                      </p>
                      <Badge variant="outline" className="font-medium  pointer-events-none">
                        {latestRecord.licence_type ?? "-"}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Token Issue
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Issue #{latestRecord.token_issue_number ?? "-"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Valid {formatDate(latestRecord.token_valid_from)} →{" "}
                        {formatDate(latestRecord.token_valid_to)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Record Created
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(latestRecord.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="bg-background rounded-lg border p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Licence Categories</h3>
                <p className="text-xs text-muted-foreground">
                  {entitlements.filter((e) => e.hasEntitlement).length} of {entitlements.length}{" "}
                  categories
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {entitlements.map((entitlement) => {
                  const CategoryTooltipContent = () => {
                    const [currentPage, setCurrentPage] = useState(0)
                    const totalPages = entitlement.entries.length
                    const currentEntry = entitlement.entries[currentPage]

                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-sm">
                            {entitlement.category} - {currentEntry.type}
                          </p>
                          {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                              >
                                <ChevronLeft className="h-3 w-3" />
                              </Button>
                              <span className="text-xs text-muted-foreground px-1">
                                {currentPage + 1}/{totalPages}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() =>
                                  setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                                }
                                disabled={currentPage === totalPages - 1}
                              >
                                <ChevronRight className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed">{currentEntry.legalLiteral}</p>
                        {entitlement.hasEntitlement && (
                          <>
                            {currentEntry.fromDate !== "-" && (
                              <p className="text-xs text-muted-foreground">
                                From: {currentEntry.fromDate}
                              </p>
                            )}
                            <p className="text-xs font-medium">
                              {currentEntry.expiryBadge.label !== "N/A"
                                ? `Valid until ${currentEntry.expiryBadge.label}`
                                : "No expiry provided"}
                            </p>
                          </>
                        )}
                        {currentEntry.restrictions && currentEntry.restrictions.length > 0 && (
                          <p className="text-xs text-muted-foreground pt-1 border-t">
                            <span className="font-medium">Restrictions:</span>{" "}
                            {currentEntry.restrictions.join(", ")}
                          </p>
                        )}
                      </div>
                    )
                  }

                  return (
                    <TooltipProvider key={entitlement.id}>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="relative"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Badge
                              className={cn("font-semibold cursor-pointer", entitlement.badgeClass)}
                            >
                              {entitlement.category}
                            </Badge>
                            {entitlement.entries.length > 1 && (
                              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] text-primary-foreground flex items-center justify-center pointer-events-none">
                                {entitlement.entries.length}
                              </span>
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-md p-4" side="top">
                          <CategoryTooltipContent />
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )
                })}
              </div>
            </div>

            <div className="bg-background rounded-lg border p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">CPC Records</h3>
                <p className="text-xs text-muted-foreground">{cpcEntries.length} entries</p>
              </div>
              {cpcEntries.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>LGV Valid To</TableHead>
                      <TableHead>PCV Valid To</TableHead>
                      <TableHead>National</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cpcEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.lgvValidTo}</TableCell>
                        <TableCell>{entry.pcvValidTo}</TableCell>
                        <TableCell>
                          {entry.national === null ? "-" : entry.national ? "Yes" : "No"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No CPC entries recorded for this licence.
                </p>
              )}
            </div>

            <div className="bg-background rounded-lg border p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Tachograph Cards</h3>
                <p className="text-xs text-muted-foreground">{tachoCards.length} cards</p>
              </div>
              {tachoCards.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Card Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Start of Validity</TableHead>
                      <TableHead>Expiry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tachoCards.map((card) => (
                      <TableRow key={card.id}>
                        <TableCell>{card.number}</TableCell>
                        <TableCell>{card.status}</TableCell>
                        <TableCell>{card.start}</TableCell>
                        <TableCell>
                          <Badge
                            className={cn(
                              "font-semibold pointer-events-none",
                              card.expiry.className,
                            )}
                          >
                            {card.expiry.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No tachograph cards recorded for this licence.
                </p>
              )}
            </div>

            <div className="bg-background rounded-lg border p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Endorsements</h3>
                <p className="text-xs text-muted-foreground">{endorsements.length} total</p>
              </div>
              {endorsements.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Offence Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Conviction Date</TableHead>
                      <TableHead>Penalty Expiry Date</TableHead>
                      <TableHead className="text-right">Penalty Points</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {endorsements.map((endorsement) => (
                      <TableRow key={endorsement.id}>
                        <TableCell>{endorsement.offenceCode}</TableCell>
                        <TableCell>{endorsement.description}</TableCell>
                        <TableCell>{endorsement.convictionDate}</TableCell>
                        <TableCell>{endorsement.expiryDate}</TableCell>
                        <TableCell className="text-right">{endorsement.penaltyPoints}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No endorsements recorded for this licence.
                </p>
              )}
            </div>

            {olderRecords.length > 0 && (
              <div className="bg-background rounded-lg border p-4">
                <h3 className="text-sm font-semibold mb-3">Historical DVLA Checks</h3>
                <Accordion type="single" collapsible className="w-full">
                  {olderRecords.map((record) => {
                    const recordEndorsements = mapEndorsements(record.endorsements)
                    return (
                      <AccordionItem key={record.id} value={record.id}>
                        <AccordionTrigger>
                          <div className="flex flex-col items-start text-left">
                            <span className="font-medium">
                              {record.driving_licence_number ?? "Unknown licence"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Verified on {formatDate(record.created_at)}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-4 pt-2">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                                  Licence Type
                                </p>
                                <p className="text-sm">{record.licence_type ?? "-"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                                  Licence Status
                                </p>
                                <p className="text-sm">{record.licence_status ?? "-"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                                  Token Valid To
                                </p>
                                <p className="text-sm">{formatDate(record.token_valid_to)}</p>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {recordEndorsements.length} endorsements, totalling{" "}
                              {recordEndorsements.reduce(
                                (total, endorsement) =>
                                  total + (Number(endorsement.penaltyPoints) || 0),
                                0,
                              )}{" "}
                              points.
                            </p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )
                  })}
                </Accordion>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

export default DrivingLicenceSection
